import { Api } from "@/api/Api";
import { QueryKey } from "@/api/index";
import { useQuery } from "@tanstack/react-query";

/**
 * This module provides types, interfaces, and a React hook to fetch and manage UK flood alerts
 * from the Meteomatics API. It includes detailed typings for the API response and utilities
 * for querying flood alerts with optional parameters.
 */

/**
 * Severity levels for UK Defra flood alerts.
 *
 * @remarks
 * - 1: Severe Flood Warning
 * - 2: Flood Warning
 * - 3: Flood Alert
 */
export type SeverityLevel = 1 | 2 | 3;

/**
 * Properties specific to the "uk-defra" source in flood alerts.
 */
export interface UKDefraProperties {
  /** ISO-8601 timestamp when the message was last changed */
  time_message_changed?: string;
  /** ISO-8601 timestamp when the alert was raised */
  time_raised?: string;
  /** ISO-8601 timestamp when the severity last changed */
  time_severity_changed?: string;
  /** Description text of the alert */
  description?: string;
  /** Name of the Environment Agency area */
  ea_area_name?: string;
  /**
   * Indicates if the alert is tidal.
   * Can be a boolean or a string as returned by the API.
   */
  is_tidal?: boolean | string;
  /** Message content of the alert */
  message?: string;
  /** Severity description, e.g. "Severe Flood Warning" */
  severity?: string;
  /** Severity level as a number where 1 is most severe */
  severity_level: SeverityLevel;
  /** Unique flood area identifier, e.g. "013FWFGM19" */
  flood_area_id: string;
}

/**
 * Base interface representing a generic flood alert.
 *
 * @template TProps - The type of the source-specific properties.
 */
export interface FloodAlertBase<TProps = Record<string, unknown>> {
  /** Source identifier, e.g., "uk-defra" */
  source: string;
  /** Full name of the source, e.g., "UK Department for Environment, Food, and Rural Affairs" */
  source_long: string;
  /** Source-specific properties */
  properties: TProps;
}

/**
 * Represents a flood alert from the UK Defra source.
 */
export type UKDefraAlert = FloodAlertBase<UKDefraProperties>;

/**
 * Response structure returned by the flood alerts API.
 */
export interface FloodAlertsResponse {
  /** Array of flood alerts */
  alerts: UKDefraAlert[];
}

/**
 * Fetch UK flood alerts (can be used outside React as well).
 */
export const fetchUKFloods = async (): Promise<FloodAlertsResponse> => {
  return await Api.apiGeneric<FloodAlertsResponse>("https://api.meteomatics.com/get_flood_alerts");
};

/**
 * React hook to fetch UK flood alerts using react-query.
 *
 * @returns An object containing the alerts array, loading state, and any error encountered.
 */
export const fetchFloods = () => {
  const query = useQuery<FloodAlertsResponse, Error>([QueryKey.UK_FLOODS], fetchUKFloods, {
    staleTime: 5 * 60 * 1000,
  });

  return {
    alerts: query.data?.alerts ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
};
